import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n";
import { scrollToSection } from "./Navbar";

const HERO_IMG =
  "https://images.unsplash.com/photo-1765158098649-d251f49ceec0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjByZWFsJTIwZXN0YXRlJTIwYnVpbGRpbmclMjBleHRlcmlvciUyMGRhcmslMjBuaWdodCUyMGdvbGQlMjBsaWdodGluZ3xlbnwwfHx8fDE3ODkwNTQ0MDB8MA&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const { t } = useLang();
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 900], [0, 260]);
  const opacityBg = useTransform(scrollY, [0, 700], [1, 0.3]);

  const lines = [
    { text: t("hero_line1"), italic: false },
    { text: t("hero_line2"), italic: false },
    { text: t("hero_line3"), italic: true },
  ];

  return (
    <section className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: yBg, opacity: opacityBg }} data-testid="hero-media-wrapper">
        <img src={HERO_IMG} alt="Architettura di pregio" className="w-full h-full object-cover scale-110" />
        <div className="absolute inset-0 bg-[#0A0A0C]/62" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-transparent to-[#0A0A0C]/70" />
      </motion.div>

      <div className="relative z-10 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto w-full pb-24 pt-40">
        <motion.img
          src="/logo.png"
          alt="Capital Stone S.r.l. — Investimenti Immobiliari, Milano"
          data-testid="hero-logo"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="h-16 sm:h-20 lg:h-24 w-auto mb-10 drop-shadow-[0_6px_24px_rgba(0,0,0,0.6)]"
        />

        <h1
          data-testid="hero-title"
          className="font-display text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05] text-zinc-100 max-w-4xl"
        >
          {lines.map((line, i) => (
            <span key={`${line.text}-${i}`} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.italic ? "italic text-gold" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay: 0.65 + i * 0.18, ease: [0.22, 1, 0.36, 1] }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.4, ease: [0.22, 1, 0.36, 1] }}
          className="text-base sm:text-lg text-zinc-400 leading-relaxed max-w-xl mt-8"
        >
          {t("hero_sub")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.6, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-wrap items-center gap-4 mt-10"
        >
          <a
            href="#operazioni"
            data-testid="hero-explore-btn"
            onClick={scrollToSection("#operazioni")}
            className="group flex items-center gap-3 bg-[#D4AF37] text-[#0A0A0C] text-xs tracking-[0.2em] uppercase font-semibold px-8 py-4 hover:bg-[#F3E5AB] transition-colors duration-300"
          >
            {t("hero_cta1")}
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a
            href="#contatti"
            data-testid="hero-contact-btn"
            onClick={scrollToSection("#contatti")}
            className="text-xs tracking-[0.2em] uppercase text-zinc-200 border border-white/20 px-8 py-4 hover:border-amber-500/60 hover:text-[#D4AF37] transition-colors duration-300"
          >
            {t("hero_cta2")}
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
        className="absolute bottom-8 right-8 lg:right-16 z-10 hidden sm:flex items-center gap-3 text-zinc-500"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase font-mono">{t("hero_scroll")}</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={14} className="text-[#D4AF37]" />
        </motion.span>
      </motion.div>
    </section>
  );
};
